import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Ban, Check, ListChecks, ShieldAlert, SlidersHorizontal } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchEvents, resolveEvent, FRAUD_STR as S, type SuspiciousEventRow } from './fraudApi'

/**
 * A25.07 — Подозрительные события (review queue). Block the user or clear the
 * event (false positive). Dormant until `/admin/antifraud/events` ships.
 * Placeholder permission USERS_VIEW.
 */
export function SuspiciousQueueScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<SuspiciousEventRow>[]>(
    () => [
      { key: 'user', header: S.user, type: 'text' },
      { key: 'type', header: S.type, type: 'text' },
      { key: 'score', header: S.score, type: 'number' },
      { key: 'reason', header: S.reason, type: 'text' },
      { key: 'created_at', header: S.created, type: 'text' },
    ],
    [],
  )

  const resolve = useCallback(
    async (row: SuspiciousEventRow, action: 'block' | 'clear') => {
      try {
        await resolveEvent(row.id, action)
        toast(action === 'block' ? S.blocked : S.cleared, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<SuspiciousEventRow>[]>(
    () => [
      {
        id: 'block',
        label: S.block,
        icon: <Ban size={14} />,
        danger: true,
        visible: (r) => r.status === 'open',
        onSelect: (r) => resolve(r, 'block'),
      },
      {
        id: 'clear',
        label: S.clear,
        icon: <Check size={14} />,
        visible: (r) => r.status === 'open',
        onSelect: (r) => resolve(r, 'clear'),
      },
    ],
    [resolve],
  )

  const fetcher = useCallback(fetchEvents, [])
  return (
    <>
      <PageHeader
        title={S.events}
        screenId="A25.07"
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/antifraud/rules"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <SlidersHorizontal size={13} />
              {S.rules}
            </Link>
            <Link
              to="/antifraud/blacklist"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <ListChecks size={13} />
              {S.blacklist}
            </Link>
          </div>
        }
      />
      <DataTable<SuspiciousEventRow>
        screenId="A25.07"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        emptyState={{ icon: <ShieldAlert size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
